/**
 * Where a public page lives in each locale, and the `hreflang` head that tells
 * search engines so.
 *
 * The default locale keeps the unprefixed URLs it has always had; every other
 * locale lives under its own prefix (`/es`, `/es/privacy`). Reciprocal
 * `hreflang` links plus an `x-default` pointing at the unprefixed page are what
 * Google asks for, and they are how a Spanish searcher lands on `/es` without
 * any redirect.
 */
import { DEFAULT_LOCALE, type Locale } from "@reflect/domain";
import type { Metadata } from "next";

import { LIVE_LOCALES } from "./liveLocales";

/** A public page's default-locale path: `/` or `/privacy`, never slashed. */
export type PublicPath = "/" | `/${string}`;

/**
 * `path` as it is spelled in `locale`.
 *
 * @param path - the page's default-locale path.
 * @param locale - the locale wanted.
 * @returns `/privacy` for the default locale, `/es/privacy` (or `/es` for the
 *   root) for any other.
 */
export function localisedPath(path: PublicPath, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/**
 * The default-locale path of whatever public page `pathname` is — the inverse
 * of {@link localisedPath}: `/es/privacy` → `/privacy`, `/es` → `/`.
 *
 * @param pathname - the current URL's path.
 * @param locales - the locales on offer. Defaults to the live locales; a seam for tests.
 */
export function defaultLocalePath(
  pathname: string,
  locales: readonly Locale[] = LIVE_LOCALES,
): PublicPath {
  const prefix = (locales as readonly string[]).find(
    (locale) =>
      locale !== DEFAULT_LOCALE &&
      (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)),
  );
  const rest = prefix ? pathname.slice(prefix.length + 1) : pathname;
  return rest === "" ? "/" : (rest as PublicPath);
}

/**
 * The page's canonical URL and its `hreflang` alternates, relative to the root
 * layout's `metadataBase`.
 *
 * Spread into a page's metadata. Nothing at all while the product has one
 * locale — an `hreflang` set naming only itself says nothing — and nothing
 * means no `alternates` key: even an `undefined` one would replace the root
 * layout's self-canonical (`rootDocument.tsx`) instead of inheriting it.
 *
 * @param path - the page's default-locale path.
 * @param locale - the locale this rendering is in.
 * @param locales - the locales on offer. Defaults to the live locales; a seam for tests.
 */
export function localeAlternates(
  path: PublicPath,
  locale: Locale,
  locales: readonly Locale[] = LIVE_LOCALES,
): Pick<Metadata, "alternates"> {
  if (locales.length < 2) return {};
  const languages: Record<string, string> = Object.fromEntries(
    locales.map((candidate) => [candidate, localisedPath(path, candidate)]),
  );
  languages["x-default"] = path;
  return { alternates: { canonical: localisedPath(path, locale), languages } };
}
